const KMA_SUBTITLE_MAX_LENGTH = 24;
const KMA_SUBTITLE_LONG_FALLBACK = "Your Neighborhood";

export function normalizeKmaDurationValue(duration) {
  const d = String(duration || "6m")
    .trim()
    .toLowerCase();
  return d === "12m" ? "12m" : "6m";
}

/**
 * Picks the keyMarketAvgsList row for the current PMA area (getPMADetails).
 * Rows are matched by `kmaAreaID` === `currentArea.mlsAreaID`.
 */
export function pickKeyMarketAvgsRowForArea(keyMarketAvgsList, currentArea) {
  if (!Array.isArray(keyMarketAvgsList) || keyMarketAvgsList.length === 0) {
    return null;
  }
  if (!currentArea) {
    return keyMarketAvgsList[0] || null;
  }

  const { mlsAreaID, areaID } = currentArea;
  const row = keyMarketAvgsList.find(
    (r) =>
      r.kmaAreaID === mlsAreaID ||
      String(r.kmaAreaID) === String(mlsAreaID) ||
      String(r.kmaAreaID) === String(areaID) ||
      r.kmaAreaID === areaID,
  );

  if (row) return row;

  if (mlsAreaID && String(mlsAreaID).includes("DEMO")) {
    return keyMarketAvgsList[0] || null;
  }

  if (keyMarketAvgsList.length === 1) {
    return keyMarketAvgsList[0];
  }

  return null;
}

export function formatKmaAreaDuration(kmaAreaDuration) {
  if (!kmaAreaDuration) {
    return "";
  }
  const d = String(kmaAreaDuration).trim().toLowerCase();
  if (d === "6m") return "Last 6 Months";
  if (d === "12m") return "Last 12 Months";
  if (d === "3m") return "Last 3 Months";
  const monthMatch = d.match(/^(\d+)m$/);
  if (monthMatch) {
    return `Last ${monthMatch[1]} Months`;
  }
  return kmaAreaDuration;
}

export function getKmaDurationLabel(kmaRow) {
  if (!kmaRow) return "";
  if (kmaRow.kmaDurationText) return kmaRow.kmaDurationText;
  return formatKmaAreaDuration(kmaRow.kmaAreaDuration || kmaRow.kmaDuration);
}

export function getSortedKmaBoxes(kmaRow) {
  if (
    !kmaRow ||
    !Array.isArray(kmaRow.kmaBoxes) ||
    kmaRow.kmaBoxes.length === 0
  ) {
    return [];
  }
  return [...kmaRow.kmaBoxes].sort(
    (a, b) => (a.kmaBoxNumber || 0) - (b.kmaBoxNumber || 0),
  );
}

export function hasKmaBoxValue(box) {
  const raw = box?.kmaValue;
  return raw != null && String(raw).trim() !== "";
}

export function formatKmaSubtitle(subtitle) {
  const text = String(subtitle || "").trim();
  if (!text) {
    return "";
  }
  if (text.length >= KMA_SUBTITLE_MAX_LENGTH) {
    return KMA_SUBTITLE_LONG_FALLBACK;
  }
  return text;
}

/** Header/meta copy when API row is missing or partial. */
export function getKmaHeaderFields(kmaRow, currentArea) {
  const area = currentArea || {};
  const neighborhood =
    area.mlsNeighborhood || area.areaName || area.mlsCityName || "";

  return {
    title: kmaRow?.kmaAreaTitle || "Key Market Averages",
    subtitle: formatKmaSubtitle(kmaRow?.kmaAreaName || neighborhood),
    tagLine: kmaRow?.kmaAreaTagLine || "",
  };
}

/** Parse API currency strings such as `"$930,000"`. */
export function parseKmaCurrencyValue(value) {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? null : num;
}

/**
 * Sale Price display (Avg/Median): under $1M → nearest $1K (`$930K`);
 * $1M+ → nearest $100K as millions with one decimal (`$1.4M`).
 */
export function formatSalePriceKmaValue(kmaValue) {
  const num = parseKmaCurrencyValue(kmaValue);
  if (num == null) {
    return kmaValue ?? "";
  }

  if (num < 1_000_000) {
    const thousands = Math.round(num / 1000);
    return `$${thousands}K`;
  }

  const roundedToHundredThousand = Math.round(num / 100_000) * 100_000;
  const millions = roundedToHundredThousand / 1_000_000;
  return `$${millions.toFixed(1)}M`;
}

export function isAvgSalePriceBox(box) {
  const id = String(box?.kmaID || "").toUpperCase();
  const title = String(box?.kmaTitle || "").toLowerCase();
  return id === "ASP" || title.includes("avg sale price");
}

export function isMedianSalePriceBox(box) {
  const id = String(box?.kmaID || "").toUpperCase();
  const title = String(box?.kmaTitle || "").toLowerCase();
  return id === "MSP" || title.includes("median sale price");
}

export function isFullCurrencyKmaValue(value) {
  const str = String(value ?? "").trim();
  if (!str.startsWith("$")) {
    return false;
  }
  if (/[KM]$/i.test(str)) {
    return false;
  }
  return parseKmaCurrencyValue(str) != null;
}

export function isSalePriceKmaBox(box) {
  return isAvgSalePriceBox(box) || isMedianSalePriceBox(box);
}

/** PMA display: K/M only for sale-price boxes with full currency values like `$2,591,000`. */
export function formatKmaBoxValue(box) {
  const raw = box?.kmaValue;
  if (raw == null || String(raw).trim() === "") {
    return "";
  }

  const str = String(raw).trim();
  if (isSalePriceKmaBox(box) && isFullCurrencyKmaValue(str)) {
    return formatSalePriceKmaValue(raw);
  }
  return str;
}

/** Modal editor: same rules as display. */
export function formatKmaValueForEditor(box) {
  return formatKmaBoxValue(box);
}

/** Persist sale-price K/M edits back to API-style `$930,000`; all other values save as entered. */
export function normalizeKmaValueForSave(box, editedValue) {
  const edited = String(editedValue ?? "").trim();
  if (!edited) {
    return edited;
  }

  if (isSalePriceKmaBox(box)) {
    const compactM = edited.match(/^\$([\d.]+)M$/i);
    if (compactM) {
      const num = Math.round(parseFloat(compactM[1]) * 1_000_000);
      return `$${num.toLocaleString("en-US")}`;
    }
    const compactK = edited.match(/^\$([\d.]+)K$/i);
    if (compactK) {
      const num = Math.round(parseFloat(compactK[1]) * 1000);
      return `$${num.toLocaleString("en-US")}`;
    }
  }

  return edited;
}

export function mergeKmaRowIntoList(keyMarketAvgsList, updatedRow, mlsAreaID) {
  if (!updatedRow) {
    return keyMarketAvgsList;
  }
  if (!Array.isArray(keyMarketAvgsList) || keyMarketAvgsList.length === 0) {
    return [updatedRow];
  }

  let found = false;
  const next = keyMarketAvgsList.map((row) => {
    if (
      row.kmaAreaID === mlsAreaID ||
      String(row.kmaAreaID) === String(mlsAreaID)
    ) {
      found = true;
      return updatedRow;
    }
    return row;
  });

  if (!found) {
    next.push(updatedRow);
  }
  return next;
}

export function cloneKmaRow(row) {
  if (!row) return null;
  return {
    ...row,
    kmaBoxes: Array.isArray(row.kmaBoxes)
      ? row.kmaBoxes.map((box) => ({ ...box }))
      : [],
  };
}

export function getBoxKey(box) {
  return box.kmaID || String(box.kmaBoxNumber);
}
